import Joi from 'joi';
import { AxiosError } from 'axios';
import api from '../api';
import { createFlightBrief, getFlightBrief } from '../flightBriefs/flightBriefsService';

const bookingSchema = Joi.object({
  id: Joi.number().required(),
  departure: Joi.object({
    airport: Joi.object({
      city: Joi.string().allow(null, ''),
      code: Joi.string(),
    }),
    date: Joi.string(),
    time: Joi.string().allow(null, ''),
    utc: Joi.string().allow(null, ''),
  }),
  arrival: Joi.object({
    airport: Joi.object({
      city: Joi.string().allow(null, ''),
      code: Joi.string(),
    }),
    date: Joi.string(),
    time: Joi.string().allow(null, ''),
    utc: Joi.string().allow(null, ''),
  }),
  createdAt: Joi.string(),
  broker: Joi.object({
    id: Joi.number(),
    firstname: Joi.string(),
    lastname: Joi.string(),
  }).allow(null),
  customer: Joi.object({
    id: Joi.number(),
    firstname: Joi.string(),
    lastname: Joi.string(),
    email: Joi.string().allow(null, ''),
  }),
  cancellationReason: Joi.string().allow(null, ''),
  hasPaymentLink: Joi.boolean(),
  extra: Joi.object({
    catering: Joi.string().allow(null, ''),
    transport: Joi.string().allow(null, ''),
    other: Joi.string().allow(null, ''),
  }).allow(null),
  passengers: Joi.object({
    adults: Joi.number(),
    children: Joi.number(),
    infants: Joi.number(),
    animals: Joi.number(),
  }),
  luggage: Joi.object({
    s: Joi.number(),
    m: Joi.number(),
    l: Joi.number(),
    xl: Joi.number(),
  }),
  note: Joi.string().allow(null, ''),
});

/**
 * Get bookings List
 * @param {object} queryParams - The query parameters.
 * @returns {Promise<
 * {total: number, pageNumber: number, data: Booking[]}>} - The list of bookings.
 */
export const getBookings = async (queryParams) => {
  // remove empty queryParams
  const cleanQueryParams = queryParams;
  Object.keys(cleanQueryParams).forEach(
    /**
     * @param {keyof typeof queryParams} key
     * @returns {void} - remove empty queryParams
     */
    (key) => {
      if (!cleanQueryParams[key]) {
        delete cleanQueryParams[key];
      }
    },
  );
  const result = await api.get('/bookings', { params: cleanQueryParams });

  try {
    const schema = Joi.object({
      data: Joi.array().items(bookingSchema),
      pageNumber: Joi.number(),
      total: Joi.number(),
    });

    await schema.validateAsync(result.data, { allowUnknown: true });
  } catch (/** @type {any} */ error) {
    throw new AxiosError('', `API response do not match booking list Schema : ${error.message}`);
  }
  return result.data;
};

/**
 * Get a booking by id
 * @param {string} id - The booking id.
 * @returns {Promise<Booking>} - The booking.
 */
export const getBookingById = async (id) => {
  const result = await api.get(`/bookings/${id}`);

  try {
    await bookingSchema.validateAsync(result.data, { allowUnknown: true });
  } catch (/** @type {any} */ error) {
    throw new AxiosError('', `API response do not match booking item Schema : ${error.message}`);
  }

  return result.data;
};

/**
 * Create a new booking
 * @param {Booking} booking
 * @returns {Promise<any>} - 201 return code
 */
export const createBooking = async (booking) => {
  // remove empty body properties
  const cleanBody = booking;
  Object.keys(cleanBody).forEach(
    /**
     * @param {keyof typeof booking} key
     * @returns {void} - remove empty queryParams
     */
    (key) => {
      if (!cleanBody[key]) {
        delete cleanBody[key];
      }
    },
  );
  const result = await api.post('/bookings', cleanBody);
  return result.data;
};

/**
 * Update a new booking
 * @param {Booking} booking
 * @returns {Promise<any>} - 201 return code
 */
export const updateBooking = async (booking) => {
  // remove empty body properties
  const cleanBody = booking;
  Object.keys(cleanBody).forEach(
    /**
     * @param {keyof typeof booking} key
     * @returns {void} - remove empty queryParams
     */
    (key) => {
      if (!cleanBody[key]) {
        delete cleanBody[key];
      }
    },
  );
  const result = await api.put(`/bookings/${booking.id}`, cleanBody);
  return result.status;
};

/**
 * Duplicate a booking
 * @param {object} params
 * @param {Booking} params.bookingToCopy - The booking to copy.
 * @param {boolean} params.withBrief - If true, the flight brief will be copied too.
 * @returns {Promise<number>} - The id of the new booking.
 */
export const duplicateBooking = async ({ bookingToCopy, withBrief }) => {
  const newBooking = await createBooking(
    Object.assign(
      bookingToCopy,
      {
        departure: {
          ...bookingToCopy.departure,
          airportCode: bookingToCopy.departure?.airport?.airportCode,
        },
        arrival: {
          ...bookingToCopy.arrival,
          airportCode: bookingToCopy.arrival?.airport?.airportCode,
        },
        customerId: bookingToCopy.customer?.id,
      },
    ),
  );
  const bookingId = newBooking.id;
  if (withBrief && bookingToCopy?.flightBriefUuid) {
    const briefToCopy = await getFlightBrief(bookingToCopy.flightBriefUuid);
    await createFlightBrief(
      Object.assign(briefToCopy, { bookingId }),
    );
  }
  return bookingId;
};

/**
 * Delete a booking document
 * @param {string} bookingId
 * @returns {Promise<number>} - 204 return code
 */
export const deleteBooking = async (bookingId) => {
  const result = await api.delete(`/bookings/${bookingId}`);
  return result.status;
};
